import React, { useState } from 'react';
import { ItineraryHeader } from './ItineraryHeader';
import { ItineraryHero } from './ItineraryHero';
import { ItineraryOverview } from './overview/ItineraryOverview';
import { ItineraryOverviewFilter, TFilterOption } from './overview/ItineraryOverviewFilter';
import { ItineraryTypes } from './types';
import { formatDateDisplay, getRelativeDaysWithOccurrence } from 'utils';
import { DestinationMarker } from './items/DestinationMarker';
import { Accommodation } from './items/Accommodation';
import { HotelMarker } from './items/HotelMarker';
import { Transfer } from './items/Transfer';
import { GroundService } from './items/GroundService';
import { Bespoke } from './items/Bespoke';
import { SvgIcon } from 'ui/SvgIcon';
import StartIcon from './icons/Start.component.svg';
import PointAIcon from './icons/PointA.component.svg';
import PointBIcon from './icons/PointB.component.svg';
import EndIcon from './icons/End.component.svg';
import classNames from 'classnames';
import countriesRawData from '../../config/data/countries.json';
import { useRouteMatch } from 'react-router';
import { useItineraryQueries } from './useItineraryQueries';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { useItinerarySaveAndPublishFlow } from './useItinerarySaveAndPublishFlow';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useSelector } from 'react-redux';
import { min, max, isNil } from 'lodash';
import { differenceInDays } from 'date-fns';
import { useElementResizeCompute } from 'hooks/useElementResizeCompute';
import { sortItineraryItemsByPosition } from './utils';

interface IItineraryPageRouteParams {
  bookingUuid: string;
  version: string;
}

export const ItineraryPage = () => {
  const match = useRouteMatch<IItineraryPageRouteParams>();
  const { bookingUuid, version } = match.params;
  const user = useSelector(AuthSelectors.getCurrentUser);
  const itineraryQueries = useItineraryQueries(makeItineraryManagerApi(), bookingUuid, version);
  const [selectedFilter, setSelectedFilter] = useState<TFilterOption>('FULL_OVERVIEW');

  const itinerarySaveAndPublishFlow = useItinerarySaveAndPublishFlow({
    bookingUuid,
    version,
  });

  const requiredOffsetForDayMarkers = useElementResizeCompute('itinerary-header', () => {
    const container = document.getElementById('itinerary-header');
    return container ? container.offsetHeight + 20 : 170; // 170 is a "sensible" default
  });

  if (itineraryQueries.getItineraryView.isFetching) {
    return (
      <div className="my-40px">
        <LoadingBar label="Loading itinerary data..." />
      </div>
    );
  }

  const bookingRef: string | undefined =
    itineraryQueries.getItineraryView.data?.data?.itinerary?.bookingReference || undefined;
  const publishedBy = itineraryQueries.getItineraryView.data?.data?.publishedBy;
  const datedItems = itineraryQueries.getItineraryView.data?.data?.itinerary?.datedItems || [];
  const undatedItems = itineraryQueries.getItineraryView.data?.data?.itinerary?.undatedItems || [];
  const hotels = itineraryQueries.getItineraryView.data?.data?.itinerary?.hotels || [];
  const headerImage: ItineraryTypes['ItineraryImageUploadOrThumbnail'] =
    itineraryQueries.getItineraryView.data?.data?.itinerary?.headerImage;
  const logoImage: ItineraryTypes['ItineraryImageUploadOrThumbnail'] =
    itineraryQueries.getItineraryView.data?.data?.itinerary?.logoImage;
  // const subtitle: string = itineraryQueries.getItineraryView.data?.data?.itinerary?.subtitle || '';
  const isTripOverviewVisible: boolean =
    itineraryQueries.getItineraryView.data?.data?.itinerary?.isTripOverviewVisible ?? false;
  const contactDetails: ItineraryTypes['UserForItineraryContact'] | undefined =
    itineraryQueries.getItineraryView.data?.data?.contact;
  // Group items by date and sort by position within each day
  const groupedByDate = datedItems.reduce((acc, item, idx) => {
    const date = item.date || '';
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push({ item, originalIndex: idx });
    return acc;
  }, {} as Record<string, Array<{ item: ItineraryTypes['ItineraryItem']; originalIndex: number }>>);

  // Sort items within each group by position
  Object.keys(groupedByDate).forEach(date => {
    // Extract items for sorting
    const items = groupedByDate[date].map(data => data.item);
    // Sort items
    const sortedItems = sortItineraryItemsByPosition([...items]);
    // Map back to original structure with originalIndex
    groupedByDate[date] = sortedItems.map(item => {
      const originalData = groupedByDate[date].find(d => d.item.uuid === item.uuid)!;
      return originalData;
    });
  });

  // Create sorted flat array of items for rendering
  const sortedDatedItems = Object.entries(groupedByDate)
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .flatMap(([, itemsData]: [string, Array<{ item: ItineraryTypes['ItineraryItem']; originalIndex: number }>]) =>
      itemsData.map(({ item }) => item)
    );

  const allDates: string[] = sortedDatedItems.map(item => item.date).filter((d): d is string => d != null);
  const days = getRelativeDaysWithOccurrence(allDates);

  const allEndDates = sortedDatedItems.map(item => item.endDate).filter((d): d is string => d != null);
  const earliestDate = min([...allDates, ...allEndDates]) as string;
  const latestDate = max([...allDates, ...allEndDates]) as string;

  const diffInDays = differenceInDays(new Date(latestDate), new Date(earliestDate));

  const totalDaysCount = diffInDays + 1;
  const totalNightsCount = diffInDays;

  const DayMarker = ({ day, idx }: { day: number; idx: number }) => {
    return (
      <h3
        className="m-0 bg-white"
        style={{
          zIndex: (idx + 1) * 10,
        }}
      >
        <span className="block text-left">
          <span className="font-noe-display text-[22px] lg:text-[26px] text-black font-normal">Day {day}</span>
          <span className="block font-hurmegeometric-sans text-12px lg:text-[14px] text-flint font-normal uppercase">
            {allDates[idx] ? formatDateDisplay(allDates[idx]) : ''}
          </span>
        </span>
      </h3>
    );
  };

  let currentAccomIndexRelativeToHotel = 0;
  let accomCountPerHotel: any = {};

  // calculate the rooms per stay
  let currentStayIndex;
  datedItems.forEach((item, index: number) => {
    if (item.itemType === ('hotelMarker' as const)) {
      currentStayIndex = index;
    }
    if (item.itemType === ('accommodation' as const)) {
      accomCountPerHotel[currentStayIndex] = accomCountPerHotel[currentStayIndex]
        ? accomCountPerHotel[currentStayIndex] + 1
        : 1;
    }
  });

  const countryNamesForHeader = Array.from(
    new Set(
      datedItems
        .filter(i => i.itemType === 'destinationMarker')
        .map(i => countriesRawData[i.countryCode]?.name)
        .filter(Boolean)
    )
  ).join(' - ');

  const isLatestVersion = publishedBy === undefined;

  const isPublshed = publishedBy !== undefined;

  // @ts-ignore - this type does exist, Axios types are wrong
  const errorResponse: number | undefined = itineraryQueries.getItineraryView.error?.response?.status;

  // you can view the itinerary if
  // - its the latest version of the itinerary
  // - if its not the latest version, but you're logged in
  const canView = isPublshed || (!isNil(user) && !isPublshed);
  if (!canView || errorResponse === 404) {
    return (
      <>
        <ItineraryHeader
          isLatestVersion={isLatestVersion}
          upload={logoImage}
          mode={'view'}
          itineraryUuid={bookingUuid}
          version={version}
          days={days}
          onPublish={() => {}}
          isPublishLoading={false}
          canViewItineraryLinks={canView}
          publishedTimestamp={publishedBy?.timestamp}
          bookingRef={bookingRef}
        />
        <div className="text-black w-full px-4 m-0 lg:container lg:mx-auto lg:w-1280px lg:px-0 font-hurmegeometric-sans text-15px leading-28px text-center uppercase children:block">
          <span>This page is not available.</span>
          <span>Please, contact the sender of this link for further assistance.</span>
        </div>
      </>
    );
  }

  const subtitle = Array.from(
    new Set(itineraryQueries.getItineraryView.data?.data?.itinerary?.hotels.map(h => h.region))
  ).join(', ');

  return (
    <div className="mb-40px">
      <ItineraryHeader
        isLatestVersion={isLatestVersion}
        upload={logoImage}
        mode={'view'}
        itineraryUuid={bookingUuid}
        version={version}
        days={days}
        onPublish={() => itinerarySaveAndPublishFlow.onPublish()}
        isPublishLoading={itinerarySaveAndPublishFlow.isPublishPending}
        canViewItineraryLinks={canView}
        publishedTimestamp={publishedBy?.timestamp}
        bookingRef={bookingRef}
      />
      <div>
        <ItineraryHero
          upload={headerImage}
          title={countryNamesForHeader}
          subtitles={[`${totalDaysCount} Days / ${totalNightsCount} Nights`, subtitle]}
        />

        {isTripOverviewVisible && (
          <div
            className={`relative
px-10px
lg:px-80px
mt-40px
`}
          >
            <h2 className="font-noe-display text-[26px] text-black font-normal mb-20px">Trip Overview</h2>
            <ItineraryOverviewFilter
              datedItems={sortedDatedItems}
              undatedItems={undatedItems}
              selectedFilter={selectedFilter}
              onFilterChange={setSelectedFilter}
            />
            <ItineraryOverview
              datedItems={sortedDatedItems}
              undatedItems={undatedItems}
              earliestDate={earliestDate}
              filter={selectedFilter}
            />
          </div>
        )}

        <div id="main-itinerary-wrapper">
          <div
            className={`relative grid mt-100px
px-10px
lg:px-80px
grid-cols-[1px_20px_1fr]
md:grid-cols-[1px_20px_1fr]
lg:grid-cols-[90px_80px_1fr]
`}
          >
            {sortedDatedItems.map((item: ItineraryTypes['ItineraryItem'], idx) => {
              const dayInfo = days[idx];
              const country = item.countryCode ? countriesRawData[item.countryCode] : undefined;
              const matchingHotel = hotels.find(hotel => hotel.uuid === item.hotelUuid);

              // everytime we hit a hotelMarker, reset the current accommodation relative index
              if (item.itemType === 'hotelMarker') {
                currentAccomIndexRelativeToHotel = 0;
              } else if (item.itemType === 'accommodation') {
                currentAccomIndexRelativeToHotel++;
              }

              const hotelRegions = hotels
                .filter(hotel => hotel.countryCode === item.countryCode)
                .map(hotel => hotel.region);

              return (
                <React.Fragment key={item.uuid}>
                  {/* spacer column */}
                  {dayInfo.isFirst && idx !== 0 && (
                    <>
                      <div></div>
                      <div className="col-spacer-row flex justify-around relative pt-[80px] lg:pt-[40px]">
                        <div className="border-r-2 border-teal-100 border-dotted absolute top-0 left-[50%] h-full" />
                      </div>
                      <div></div>
                    </>
                  )}

                  {/* col 1 - the day number (or spacer) */}
                  <div
                    style={{
                      top: `${requiredOffsetForDayMarkers}px`,
                    }}
                    className="col-1-desktop hidden sticky lg:block"
                  >
                    {dayInfo.isFirst && <DayMarker day={dayInfo.day} idx={idx} />}
                  </div>

                  {/* col 1 on mobile */}
                  <div
                    style={{
                      top: `${requiredOffsetForDayMarkers}px`,
                    }}
                    className={classNames('col-1-mobile z-20 lg:hidden sticky h-[100px]', {
                      '-mt-[30px] lg:mt-0': dayInfo.isFirst,
                    })}
                  >
                    {dayInfo.isFirst && (
                      // this width aligns with the width of the column
                      <span className="block w-70px text-left bg-white -ml-6px pl-6px">
                        <DayMarker day={dayInfo.day} idx={idx} />
                      </span>
                    )}
                  </div>

                  {/* col 2 - the icons and line */}
                  <div
                    className={classNames('col-2 flex justify-around relative', {
                      'mt-[40px] lg:mt-0': dayInfo.isFirst,
                    })}
                  >
                    <div className="border-r-2 border-teal-100 border-dotted absolute top-0 left-[50%] h-full" />
                    <span className="z-10 -ml-2px lg:ml-2px -mt-4px">
                      <SvgIcon
                        IconComponent={item.itemType === 'destinationMarker' ? StartIcon : PointAIcon}
                        className="w-[26px] fill-none stroke-none"
                      />
                    </span>
                    {item.time && item.itemType !== 'destinationMarker' && item.itemType !== 'hotelMarker' && (
                      <span
                        className={classNames(
                          'font-hurmegeometric-sans text-14px uppercase absolute bg-white py-5px text-flint top-22px'
                        )}
                      >
                        {item.time}
                      </span>
                    )}
                  </div>

                  {/* col 3 - the block of information for this item */}
                  <div
                    id={dayInfo.isFirst ? `itinerary-day-${dayInfo.day}` : undefined}
                    className={classNames('col-3 font-pt-sans text-black text-16px leading-19px pl-[50px] lg:pl-0', {
                      'mt-[40px] lg:mt-0': dayInfo.isFirst,
                    })}
                  >
                    {item.itemType === 'destinationMarker' && (
                      <DestinationMarker item={item} countryName={country?.name} regions={hotelRegions} />
                    )}
                    {item.itemType === 'hotelMarker' && (
                      <HotelMarker totalAccomCount={accomCountPerHotel[idx]} item={item} hotel={matchingHotel} />
                    )}

                    {item.itemType === 'transfer' && <Transfer item={item} />}

                    {item.itemType === 'accommodation' && (
                      <Accommodation item={item} roomRelativeIndex={currentAccomIndexRelativeToHotel} />
                    )}

                    {item.itemType === 'groundService' && <GroundService item={item} />}

                    {item.itemType === 'bespoke' && <Bespoke item={item} />}
                  </div>
                </React.Fragment>
              );
            })}

            {/* extra item just for the end of itinerary */}
            <>
              {/* col 1 - the day number (or spacer) */}
              <div
                style={{
                  top: `${requiredOffsetForDayMarkers}px`,
                }}
                className="col-1-desktop hidden sticky lg:block"
              ></div>

              {/* col 1 on mobile */}
              <div className={classNames('col-1-mobile z-20 lg:hidden sticky h-[100px]', {})}></div>

              {/* col 2 - the icons and line */}
              <div className={classNames('col-2 flex justify-around relative', {})}>
                <div className="border-r-2 border-teal-100 border-dotted absolute top-0 left-[50%] h-[20px]" />

                <span className="z-10 -ml-2px lg:ml-2px -mt-4px">
                  <SvgIcon IconComponent={EndIcon} className="w-[26px] fill-none stroke-none" />
                </span>
              </div>

              {/* col-3 */}
              <div>
                <h2 className="col-3 m-0 font-hurmegeometric-sans uppercase text-flint text-[14px] pl-[50px] lg:pl-0">
                  End Of Itinerary
                </h2>
              </div>
            </>
          </div>

          {undatedItems.length >= 1 && (
            <>
              <h2 className="font-noe-display text-black font-normal text-[26px] mb-10px mt-50px px-10px lg:px-80px">
                Unscheduled Products
              </h2>
              <div
                className={`relative grid mt-100px
px-10px
lg:px-80px
grid-cols-[1px_20px_1fr]
md:grid-cols-[1px_100px_1fr]
lg:grid-cols-[90px_80px_1fr]
`}
              >
                {undatedItems.map((item: ItineraryTypes['ItineraryItem'], idx) => {
                  const matchingHotel = hotels.find(hotel => hotel.uuid === item.hotelUuid);

                  // everytime we hit a hotelMarker, reset the current accommodation relative index
                  if (item.itemType === 'hotelMarker') {
                    currentAccomIndexRelativeToHotel = 0;
                  } else if (item.itemType === 'accommodation') {
                    currentAccomIndexRelativeToHotel++;
                  }

                  return (
                    <React.Fragment key={item.uuid}>
                      {/* col 1 - the day number (or spacer) */}
                      <div
                        style={{
                          top: `${requiredOffsetForDayMarkers}px`,
                        }}
                        className="col-1-descktop hidden sticky lg:block"
                      ></div>

                      {/* col 1 on mobile */}
                      <div
                        style={{
                          top: `${requiredOffsetForDayMarkers}px`,
                        }}
                        className={classNames('col-1-mobile z-20 lg:hidden sticky h-[100px]')}
                      ></div>

                      {/* col 2 - the icons and line */}
                      <div className={classNames('col-2 flex justify-around relative')}>
                        <span className="z-10 -ml-2px lg:ml-2px -mt-4px">
                          <SvgIcon IconComponent={PointBIcon} className="w-[26px] fill-none stroke-none" />
                        </span>
                        {item.time && item.itemType !== 'destinationMarker' && item.itemType !== 'hotelMarker' && (
                          <span
                            className={classNames(
                              'font-hurmegeometric-sans text-14px uppercase absolute bg-white py-5px text-flint top-22px'
                            )}
                          >
                            {item.time}
                          </span>
                        )}
                      </div>

                      {/* col 3 - the block of information for this item */}
                      <div
                        className={classNames('col-3 font-pt-sans text-black text-16px leading-19px pl-[50px] lg:pl-0')}
                      >
                        {item.itemType === 'hotelMarker' && (
                          <HotelMarker totalAccomCount={accomCountPerHotel[idx]} item={item} hotel={matchingHotel} />
                        )}
                        {item.itemType === 'transfer' && <Transfer item={item} />}
                        {item.itemType === 'accommodation' && (
                          <Accommodation item={item} roomRelativeIndex={currentAccomIndexRelativeToHotel} />
                        )}
                        {item.itemType === 'groundService' && <GroundService item={item} />}
                        {item.itemType === 'bespoke' && <Bespoke item={item} />}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* the contact details */}
      {contactDetails && (
        <div id="itinerary-contact" className="mx-10px lg:mx-80px ml-7 mt-[50px]">
          <h2 className="m-0 font-noe-display text-26px text-black font-normal mb-25px">Contact</h2>
          <div className="bg-gray-3 w-full p-20px">
            <div className="bg-white p-10px">
              <div className="grid grid-cols-[140px_1fr] text-black text-14px gap-[5px]">
                <span className="uppercase font-bold text-flint">Name</span>
                <span className="text-flint">
                  {contactDetails.title} {contactDetails.firstName} {contactDetails.lastName}
                </span>
                <span className="uppercase font-bold text-flint">Company</span>
                <span className="text-flint">{contactDetails.companyName}</span>
                {contactDetails.mobileNumber && contactDetails.mobileNumber !== '' && (
                  <>
                    <span className="uppercase font-bold text-flint">Mobile</span>
                    <span className="text-flint">{contactDetails.mobileNumber}</span>
                  </>
                )}
                {contactDetails.phoneNumber && contactDetails.phoneNumber !== '' && (
                  <>
                    <span className="uppercase font-bold text-flint">Phone Number</span>
                    <span className="text-flint">{contactDetails.phoneNumber}</span>
                  </>
                )}
                <span className="uppercase font-bold text-flint">Email</span>
                <span className="text-flint">{contactDetails.email}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {itinerarySaveAndPublishFlow.render()}
    </div>
  );
};
